import {
  Menu, MenuItem, makeStyles,
} from '@material-ui/core';
import React, { useCallback, useMemo, useState } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import NumberFormat from 'react-number-format';
import PropTypes from 'prop-types';
import InfoIcon from '@material-ui/icons/Info';
import clsx from 'clsx';
import Label from '../../../../Label';
import AgentCommission from '../../../AgentCommission';
import getCurrencySymbol from '../../../../../helpers/currencySymbol';
import { FlightFareSources } from '../../../../../constants';

const MenuStyles = makeStyles((theme) => ({
  selected: {
    color: theme.palette.common.white,
    backgroundColor: `${theme.palette.primary.main}!important`,
  },
  root: {
    fontFamily: 'var(--fontFamilyBody)',
    display: 'block',
  },
}));

const FlightFareClasses = ({ flightItinerary, handleAddToTripClick }) => {
  const classes = MenuStyles();

  const [anchorEl, setAnchorEl] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});

  const handleToggleExpand = (event, index) => {
    setExpandedItems((prevExpandedItems) => ({
      ...prevExpandedItems,
      [index]: !prevExpandedItems[index],
    }));
    event.stopPropagation();
  };
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const setSelectedFare = (event, fareIndex) => {
    if (handleAddToTripClick) {
      handleAddToTripClick(event, fareIndex);
      handleClose();
    }
  };

  const flightsList = useMemo(() => flightItinerary.flightSegments
    .map((segment) => segment.flights).flat(), [flightItinerary.flightSegments]);

  const getGroupedFlights = useCallback((index) => {
    const groupedFares = flightsList.reduce((acc, flight) => {
      const currentFare = flight.allFares[index];
      if (currentFare && currentFare.priceClassDescriptions
        && currentFare.priceClassDescriptions.length > 0) {
        const fareKey = `${currentFare.fareBrandName}-${currentFare.priceClassDescriptions.join('-')}`;
        if (!acc[fareKey]) {
          acc[fareKey] = {
            fareBrandName: currentFare.fareBrandName,
            priceClassDescriptions: currentFare.priceClassDescriptions,
            flights: [],
          };
        }
        acc[fareKey].flights.push(flight);
      }
      return acc;
    }, {});
    return groupedFares;
  }, [flightsList]);

  const viewFareDetails = useCallback((fare) => (
    <ul className="mt-2 text-sm list-disc pl-5 text-wrap">
      {fare.priceClassDescriptions.map((description) => (
        <li
          className="whitespace-normal break-words text-wrap text-left"
          key={description}
        >
          {description}
        </li>
      ))}
    </ul>
  ), []);

  const formatFlightFareKey = (fare) => (`${fare.fareBasisCode}-${fare.isRefundable}-${fare.price}-${fare.pricingSubsource}-${fare.fareBrandId}-${(fare.offer || {}).offerId}`);

  const currencySymbol = getCurrencySymbol((flightItinerary.cost || {}).currencyCode);
  const ChevronIcon = anchorEl ? FaChevronUp : FaChevronDown;
  return (
    <>
      <button type="button" onClick={handleClick} className="inline-block text-center ml-5">
        Show More Classes
        <ChevronIcon className="inline-block ml-1" />
      </button>
      <Menu
        className="modal-safe-click-zone"
        PaperProps={{
          style: {
            border: '1px solid #d3d4d5',
            width: '44ch',
            maxHeight: 'calc(70% - 96px)',
          },
        }}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        id="customized-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {flightItinerary.flightSegments[0].flights[0].allFares.map((fare, index) => (
          <MenuItem
            component="li"
            key={`${index.toString()}`}
            selected={formatFlightFareKey(flightItinerary.flightSegments[0].flights[0].selectedFare)
               === formatFlightFareKey(fare)}
            classes={classes}
            onClick={(event) => { setSelectedFare(event, index); }}
          >
            <div className={clsx('flex justify-between w-full mb-1', fare.agentCommission > 0 ? 'gap-2' : 'gap-1')}>
              <div className="whitespace-normal">
                {flightItinerary.alternateFareCabinClassNames
                  && flightItinerary.alternateFareCabinClassNames[index]}
              </div>
              <div className="text-right">
                <NumberFormat value={fare.price} displayType="text" thousandSeparator prefix={currencySymbol} fixedDecimalScale="true" decimalScale="2" />
                {' '}
                {fare.fareAcccountCode ? (
                  <Label
                    icon={<InfoIcon style={{ fontSize: '20px' }} />}
                    level="success"
                    italic
                    inline
                    descriptionOnHover={`${fare.fareAcccountCode} rate code match with your selection.`}
                  />
                ) : <></>}
              </div>
            </div>
            <div className={clsx('text-xs flex flex-wrap justify-between items-center', fare.agentCommission > 0 ? 'gap-2' : 'gap-1')}>
              <Label
                text={(
                  <>
                    {fare.pricingSubsource && fare.pricingSubsource === FlightFareSources.NDC ? (
                      <>
                        {`${fare.refundabilityLabel},`}
                        <b> NDC Fare</b>
                      </>
                    ) : fare.refundabilityLabel}
                    {fare.fareDescription && `, ${fare.fareDescription}`}
                  </>
                )}
                level=""
                inline
                tooltipPlacement="top"
                descriptionOnHover={fare.partialRefundPolicyText}
                className="flex-auto"
              />
              {fare.agentCommission > 0 ? (
                <AgentCommission mode={flightItinerary.mode} agentCommission={fare.agentCommission} currencyCode={flightItinerary.cost && flightItinerary.cost.currencyCode} className="py-1 inline-flex" />
              ) : <></>}
              {fare.priceClassDescriptions && fare.priceClassDescriptions.length && (
              <a
                onClick={(event) => handleToggleExpand(event, index)}
                role="button"
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    handleToggleExpand(event, index);
                  }
                }}
                tabIndex="0"
                className="underline cursor-pointer"
              >
                <span className="flex items-center gap-1">
                  {' '}
                  view rules
                  {expandedItems[index] ? (
                    <FaChevronUp className="inline-block h-3" />
                  ) : (
                    <FaChevronDown className="inline-block h-3" />
                  )}
                </span>
              </a>
              )}
            </div>
            <div className="w-full">
              {flightsList.length === 1 ? (
                <div className="w-full">
                  {expandedItems[index] && fare.priceClassDescriptions && viewFareDetails(fare)}
                </div>
              ) : (
                (Object.values(getGroupedFlights(index)).map((currentFare, fareIndex) => (
                  <div key={`${currentFare.fareBrandName}-${fareIndex.toString()}`}>
                    {expandedItems[index] && currentFare.priceClassDescriptions && (
                    <>
                      <p className="p-1 mt-1 text-xs leading-3 font-semibold whitespace-normal break-words text-wrap">
                        {currentFare.fareBrandName}
                        {' '}
                        (
                        {currentFare.flights.map((flight) => (
                          `${flight.originAirportCode} - ${flight.destinationAirportCode}`
                        )).join(', ')}
                        )
                      </p>
                      {viewFareDetails(currentFare)}
                    </>
                    )}
                  </div>
                )))
              )}
            </div>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

FlightFareClasses.propTypes = {
  flightItinerary: PropTypes.shape().isRequired,
  handleAddToTripClick: PropTypes.func.isRequired,
};

export default React.memo(FlightFareClasses);
